import { useEffect } from 'react'
import { Sparkles, Star, Ruler } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import confetti from 'canvas-confetti'
import { isGamificationEnabled } from '@tfa/shared'
import { useAuth } from '../../hooks/useAuth'
import { measurementsReward } from '../../utils/measurementsReward'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
  /** Замеры за прошлый день — XP не начисляется (bug #221): скрыть чип, дать пояснение. */
  pastDay?: boolean
  /**
   * #526: сколько XP реально начислил сервер за эту сдачу (ответ POST
   * /diary/measurements). Раньше экран рисовал «+1 XP» хардкодом — и число было
   * не то, и показывалось оно даже когда начисления не было.
   */
  xpAwarded?: number | null
}

const SPARKS = [
  { top: -14, left: 18, rotate: -20, delay: 0 },
  { top: -10, right: 16, rotate: 15, delay: 0.08 },
  { top: 30, left: -14, rotate: -35, delay: 0.14 },
  { top: 28, right: -12, rotate: 30, delay: 0.2 },
]

export function MeasurementsCongrats({ open, onClose, pastDay = false, xpAwarded }: Props) {
  const { user } = useAuth()
  const rewardsOn = isGamificationEnabled((user as any)?.mode ?? null, (user as any)?.completedAt ?? null)
  const reward = measurementsReward({ rewardsOn, xpAwarded, pastDay })
  useEffect(() => {
    if (!open) return
    const colors = ['#6C5CE7', '#A29BFE', '#FFD60A', '#34C759', '#FF9500']
    const end = Date.now() + 1800
    const frame = () => {
      confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0, y: 0.65 }, colors })
      confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1, y: 0.65 }, colors })
      if (Date.now() < end) requestAnimationFrame(frame)
    }
    frame()
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 400,
            background: 'rgba(10,18,40,0.62)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, fontFamily: F,
          }}
        >
          <motion.div
            data-testid="modal-measurements-congrats"
            initial={{ scale: 0.82, opacity: 0, y: 24 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.88, opacity: 0, y: 12 }}
            transition={{ type: 'spring', damping: 20, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 340,
              borderRadius: 28, padding: '32px 24px 28px',
              background: 'rgba(255,255,255,0.96)',
              backdropFilter: 'blur(32px)',
              WebkitBackdropFilter: 'blur(32px)',
              boxShadow: '0 28px 72px rgba(108,92,231,0.28), 0 0 0 1px rgba(255,255,255,0.6)',
              textAlign: 'center', position: 'relative', overflow: 'visible',
            }}
          >
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 4 }}>
              <div
                style={{
                  width: 120, height: 120, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 12px 32px rgba(108,92,231,0.35)',
                }}
              >
                <Ruler size={56} color="white" strokeWidth={2.2} />
              </div>
              {SPARKS.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: s.delay, type: 'spring', stiffness: 400 }}
                  style={{
                    position: 'absolute',
                    top: s.top,
                    left: (s as { left?: number }).left,
                    right: (s as { right?: number }).right,
                    transform: `rotate(${s.rotate}deg)`,
                  }}
                >
                  <Sparkles size={18} color="#FFD60A" fill="#FFD60A" />
                </motion.div>
              ))}
            </div>

            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1C2D41', margin: '12px 0 6px', lineHeight: 1.2 }}>
              Замеры зафиксированы!
            </h1>
            <p style={{ fontSize: 14, color: '#6B7B8D', margin: '0 0 20px', lineHeight: 1.45 }}>
              Отлично, замеры зафиксированы.
              <br />
              Следующий замер через 7 дней.
            </p>

            {reward.kind === 'xp' && (
              <motion.div
                data-testid="measurements-xp-chip"
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.18, type: 'spring', stiffness: 400 }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  background: 'linear-gradient(135deg,#FFD60A,#FF9500)',
                  borderRadius: 22, padding: '9px 20px', marginBottom: 24,
                  boxShadow: '0 6px 18px rgba(255,149,0,0.35)',
                }}
              >
                <Star size={16} color="white" fill="white" />
                <span style={{ fontSize: 16, fontWeight: 800, color: 'white' }}>+{reward.xp} XP</span>
              </motion.div>
            )}
            {reward.kind === 'past-day' && (
              <p style={{ fontSize: 13, color: '#9AA5B4', margin: '0 0 20px', lineHeight: 1.4 }}>
                За прошлые дни прогресс не начисляется
              </p>
            )}
            {reward.kind === 'repeat' && (
              <p data-testid="measurements-no-xp-note" style={{ fontSize: 13, color: '#9AA5B4', margin: '0 0 20px', lineHeight: 1.4 }}>
                Замеры за сегодня уже засчитаны — повторная сдача без начисления
              </p>
            )}

            <button
              data-testid="btn-close-measurements-congrats"
              onClick={onClose}
              style={{
                display: 'block', width: '100%', height: 52,
                borderRadius: 16, border: 'none',
                background: 'linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)',
                color: 'white', fontSize: 16, fontWeight: 700,
                cursor: 'pointer', fontFamily: F,
                boxShadow: '0 8px 24px rgba(108,92,231,0.32)',
              }}
            >
              Продолжить
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
